import { FC, useState, useEffect, useCallback } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useDispatch } from 'react-redux';

// API
import apibridge from '../../../../apibridge';

// REDUX
import { ToastMessagesSlice } from '../../../../store/slice/ToastMessages';

// COMPONENTS
import SvgMask from '../../../../components/_Helpers/SvgMask';

// UTILS
import { guid } from '../../../../libs/utils';

// TYPES
import { AssessmentStudentSearchStudent } from '../../../../api/models';

interface StudentSearchDropdownProps {
	selectedStudent: AssessmentStudentSearchStudent | null;
	onStudentSelect: (student: AssessmentStudentSearchStudent) => void;
	placeholder: string;
	disabled?: boolean;
	excludeStudentIds?: string[];
}

const StudentSearchDropdown: FC<StudentSearchDropdownProps> = ({
	selectedStudent,
	onStudentSelect,
	placeholder,
	disabled = false,
	excludeStudentIds = []
}) => {
	const dispatch = useDispatch();

	const [searchedStudents, setSearchedStudents] = useState<AssessmentStudentSearchStudent[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

	// Cleanup function to cancel pending searches
	const cleanupSearch = useCallback(() => {
		if (searchTimeout) {
			clearTimeout(searchTimeout);
			setSearchTimeout(null);
		}
	}, [searchTimeout]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			cleanupSearch();
		};
	}, [cleanupSearch]);

	const getAssessmentStudentSearchData = async (classId?: string, searchTerm?: string) => {
		// Prevent multiple simultaneous requests
		if (isLoading) return;

		setIsLoading(true);

		try {
			const response = await apibridge.getAssessmentStudentSearch(classId || '', searchTerm || '');

			if (response && response.data) {
				if (!response.data.isError && response.data.result) {
					const allStudents = response.data.result.students || [];

					// Filter out excluded students
					const availableStudents = allStudents.filter(
						(student: AssessmentStudentSearchStudent) => !excludeStudentIds.includes(student.id || '')
					);

					setSearchedStudents(availableStudents);
				} else if (response.data.validationErrors) {
					for (const err of response.data.validationErrors) {
						dispatch(
							ToastMessagesSlice.actions.add({
								id: guid(),
								type: 'danger',
								heading: 'Assessment List error',
								description: err.reason || 'Unknown error'
							})
						);
					}
				}
			}
		} catch (error) {
			console.error('Error fetching student search data:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleSearchChange = (value: string) => {
		setSearchTerm(value);

		// Clear any existing timeout
		cleanupSearch();

		if (value.length >= 2) {
			// Debounce the search to prevent rapid API calls
			const timeout = setTimeout(() => {
				getAssessmentStudentSearchData('', value);
			}, 300); // 300ms debounce delay

			setSearchTimeout(timeout);
		} else {
			setSearchedStudents([]);
		}
	};

	const handleStudentSelect = (student: AssessmentStudentSearchStudent) => {
		onStudentSelect(student);
		setSearchTerm('');
		setSearchedStudents([]);
		cleanupSearch();
	};

	const handleDropdownToggle = (isOpen: boolean) => {
		if (!isOpen) {
			setSearchTerm('');
			setSearchedStudents([]);
			cleanupSearch();
		}
	};

	return (
		<Dropdown onToggle={handleDropdownToggle}>
			<Dropdown.Toggle className="custom-search-toggle" disabled={disabled}>
				<span className={selectedStudent ? 'text-dark-text fw-normal' : 'text-shades-500 fw-normal'}>
					{selectedStudent ? `${selectedStudent.firstName} ${selectedStudent.lastName}` : placeholder}
				</span>

				<SvgMask path="/svg/chevron-down.svg" className="text-shades-500" width={16} height={16} />
			</Dropdown.Toggle>

			<Dropdown.Menu className="custom-search-dropdown">
				<div className="search-input-wrapper">
					<input
						type="text"
						className="form-control"
						placeholder="Search for a student..."
						value={searchTerm}
						onChange={(e) => handleSearchChange(e.target.value)}
						autoFocus
					/>
				</div>

				<div className="search-results">
					{isLoading ? (
						<div className="search-hint">
							<p className="mb-0 body-small text-shades-500">Searching...</p>
						</div>
					) : searchedStudents.length > 0 ? (
						searchedStudents.map((student) => (
							<Dropdown.Item
								key={student.id}
								className="search-result-item"
								onClick={() => handleStudentSelect(student)}
							>
								<div className="student-info">
									<p className="mb-0 body-medium text-dark-text fw-semibold">
										{student.firstName} {student.lastName}
									</p>
									<p className="mb-0 body-small text-shades-500">{student.className}</p>
								</div>
							</Dropdown.Item>
						))
					) : searchTerm.length >= 2 ? (
						<div className="no-results">
							<p className="mb-0 body-small text-shades-500">No students found</p>
						</div>
					) : searchTerm.length > 0 ? (
						<div className="search-hint">
							<p className="mb-0 body-small text-shades-500">Please type at least 2 characters to search</p>
						</div>
					) : null}
				</div>
			</Dropdown.Menu>
		</Dropdown>
	);
};

export default StudentSearchDropdown;
